import { Component, type ErrorInfo, type ReactNode } from "react";
import styled from "styled-components";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * 捕获渲染过程中的异常并保留最小限度的提示。
 *
 * 此婚礼邀请函包含高德地图等第三方服务。
 * 如果没有错误边界，一旦第三方服务在渲染时抛出异常，React 将卸载整个
 * 树，只留下白屏。宾客将无法看到礼堂位置、账户号码，也无法知道出了什么问题。
 *
 * 使用类组件是因为目前还没有对应 componentDidCatch 的 Hook。
 * 这是 React 提供的唯一方法。
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* 在生产环境中保留在控制台中以供追踪原因。 */
    console.error("渲染婚礼邀请函时出错。", error, info);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <Fallback role="alert">
        <Title>出现临时性错误</Title>
        <Message>
          刷新页面后可能可以正常显示。
          <br />
          如果问题仍然存在，请告知新郎或新娘。
        </Message>
        <RetryButton type="button" onClick={() => window.location.reload()}>
          刷新
        </RetryButton>
      </Fallback>
    );
  }
}

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.layout.contentInset};
  background-color: ${({ theme }) => theme.color.background};
  text-align: center;
`;

const Title = styled.h1`
  margin-bottom: ${({ theme }) => theme.space[4]};
  color: ${({ theme }) => theme.color.accent};
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const Message = styled.p`
  margin-bottom: ${({ theme }) => theme.space[8]};
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const RetryButton = styled.button`
  min-height: 44px;
  padding: 0 ${({ theme }) => theme.space[6]};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.accentSurface};
  color: ${({ theme }) => theme.color.accent};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;
